/**
 * Tenant work presence — which kinds of AI output this workspace has ever
 * produced. Used ONLY to grow the starter navigation rail (see
 * `GROWN_NAV_ITEMS`): a brand-new workspace shouldn't be asked to navigate
 * four empty pipeline pages, and a workspace that has produced a patch should
 * never have to go looking for the patch list.
 *
 * This is presentation state, never authorization. Every route stays protected
 * server-side, and a user can pin any permitted row manually whatever this
 * says.
 */

export const NAV_PRESENCE_SIGNALS = [
  "missions",
  "workspaces",
  "patches",
  "sandboxRuns",
] as const;
export type NavPresenceSignal = (typeof NAV_PRESENCE_SIGNALS)[number];

export type NavPresence = Record<NavPresenceSignal, boolean>;

/**
 * The degraded default: everything present, i.e. hide nothing.
 *
 * Deliberately permissive. A failed/absent presence read must never take a row
 * away from someone who has been using it — the failure mode we accept is a
 * new workspace briefly seeing four empty pages, not an established one losing
 * its patch list.
 */
export const DEFAULT_NAV_PRESENCE: NavPresence = {
  missions: true,
  workspaces: true,
  patches: true,
  sandboxRuns: true,
};

/** Nothing produced yet — the shape a genuinely empty workspace resolves to. */
export const EMPTY_NAV_PRESENCE: NavPresence = {
  missions: false,
  workspaces: false,
  patches: false,
  sandboxRuns: false,
};

/** Coerce an untrusted payload (API response) into a full presence record. */
export function coerceNavPresence(value: unknown): NavPresence {
  if (!value || typeof value !== "object") return { ...DEFAULT_NAV_PRESENCE };
  const raw = value as Record<string, unknown>;
  const out = {} as NavPresence;
  for (const key of NAV_PRESENCE_SIGNALS) {
    // Only an explicit `false` hides a row; anything unrecognised degrades to
    // "present" so a shape change can never shrink somebody's rail.
    out[key] = raw[key] === false ? false : true;
  }
  return out;
}
